import { useState } from 'react';
import { supabase } from '../services/supabase';

const SESSION_KEY = 'tapnpay.staff.session.v1';

export interface StaffSession {
  employeeId: string;
  storeId: string;
  name: string;
  role: string;
  /** Token HMAC court-vécu (tapnpay-staff-pin-session) pour les fonctions
   *  employé-authentifiées (ex: encaissement cash scellé fiscalement).
   *  Optionnel : si absent, les actions qui l'exigent demandent une reconnexion. */
  token?: string;
}

async function sha256hex(raw: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw.trim()));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function useAuth() {
  const [session, setSession] = useState<StaffSession | null>(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as StaffSession) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = async (pin: string, storeId?: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const hashed = await sha256hex(pin);
      const { data, error: rpcError } = await supabase.rpc('login_with_pin', {
        pin_code_input: hashed,
        p_store_id: storeId ?? null,
      });
      if (rpcError || !data) {
        setError('PIN incorrect ou compte introuvable');
        return false;
      }
      const s: StaffSession = {
        employeeId: (data as Record<string, unknown>).id as string,
        storeId: (data as Record<string, unknown>).store_id as string,
        name: ((data as Record<string, unknown>).full_name ?? (data as Record<string, unknown>).name ?? 'Employé') as string,
        role: ((data as Record<string, unknown>).role ?? 'waiter') as string,
      };
      // Best-effort : obtenir un token signé pour les fonctions employé-authentifiées
      // (encaissement cash scellé). Si l'échange échoue, le login reste valide ;
      // CashPaymentPage demandera une reconnexion si le token manque.
      try {
        const { data: ts } = await supabase.functions.invoke('tapnpay-staff-pin-session', {
          body: { pin: hashed, store_id: storeId ?? s.storeId },
        });
        const tok = (ts as { token?: string } | null)?.token;
        if (tok) s.token = tok;
      } catch {
        /* token optionnel — ne bloque pas la connexion */
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
      setSession(s);
      return true;
    } catch {
      setError('Erreur de connexion');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
  };

  return { session, loading, error, login, logout };
}
